import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../providers/AppProvider';
import { Card } from 'react-bootstrap';
import React from 'react';

// Register eCharts components
echarts.use([TooltipComponent, GridComponent, BarChart, CanvasRenderer]);

// Interface for ML Bias Data
export interface BiasMLDataItem {
  Attribute: string;
  DisparityRatio: number;
  CalculatedImpact?: number;
  BiasDetected?: boolean;
  ExternalCompliance?: string;
  InternalPolicyCompliance?: string;
  Risk?: string;
  Action?: string;
  BiasDrift?: string;
  IntersectionalBias?: string;
}

interface BiasMLChartProps {
  data: BiasMLDataItem[] | undefined | null;
  title: string;
  description: string;
  /** When true, render chart only (no outer Card) for use inside accordions / panels */
  embedded?: boolean;
}

const BiasMLChart: React.FC<BiasMLChartProps> = ({ data, title, description, embedded }) => {
  const { getThemeColor } = useAppContext();

  // Utility to safely get theme color with fallback
  const getSafeThemeColor = (key: string, fallback: string) => {
    const color = getThemeColor?.(key);
    return color || fallback;
  };

  const borderColor = getSafeThemeColor('border-color', '#ddd');
  const tooltipBg = getSafeThemeColor('body-highlight-bg', '#ffffff');
  const tooltipText = getSafeThemeColor('light-text-emphasis', '#000');
  const axisLabelColor = getSafeThemeColor('secondary-color', '#666');

  // Safely handle data - ensure it's an array before mapping
  const safeData = Array.isArray(data) ? data : [];
  const disparityRatios = safeData.map(item => item.DisparityRatio || 0);
  const calculatedImpacts = safeData.map(item => item.CalculatedImpact || 0);
  const attributes = safeData.map(item => item.Attribute || 'Unknown');

  // Determine bar color based on risk level
  const getBarColor = (item: BiasMLDataItem) => {
    const risk = item.Risk?.toLowerCase() || '';
    if (risk === 'high') return '#E74C3C';
    if (risk === 'medium') return '#F39C12';
    return '#2ECC71'; // Low or default
  };

  const barColors = safeData.map(item => getBarColor(item));

  const options = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: tooltipBg,
      borderColor: borderColor,
      textStyle: { color: tooltipText },
      formatter: (params: any) => {
        const param = params[0];
        const dataIndex = param.dataIndex;
        const item = safeData[dataIndex];
        return `
          <strong>${item.Attribute}</strong><br/>
          Disparity Ratio: ${item.DisparityRatio?.toFixed(3) || 'N/A'}<br/>
          Calculated Impact: ${item.CalculatedImpact?.toFixed(3) || 'N/A'}<br/>
          Risk: ${item.Risk || 'N/A'}<br/>
          Bias Detected: ${item.BiasDetected ? 'Yes' : 'No'}<br/>
          Action: ${item.Action || 'N/A'}
        `;
      }
    },
    legend: {
      data: ['Disparity Ratio', 'Calculated Impact'],
      textStyle: { color: axisLabelColor },
      top: '5%'
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: attributes,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: {
        color: axisLabelColor,
        rotate: 45,
        interval: 0
      }
    },
    yAxis: {
      type: 'value',
      name: 'Value',
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: axisLabelColor },
      nameTextStyle: { color: axisLabelColor }
    },
    series: [
      {
        name: 'Disparity Ratio',
        type: 'bar',
        data: disparityRatios.map((value, idx) => ({
          value,
          itemStyle: { color: barColors[idx] }
        })),
        barWidth: '35%'
      },
      {
        name: 'Calculated Impact',
        type: 'bar',
        data: calculatedImpacts,
        barWidth: '35%',
        itemStyle: { color: '#3498DB' }
      }
    ]
  };

  const chartBlock =
    safeData.length > 0 ? (
      <ReactEChartsCore
        echarts={echarts}
        option={options}
        style={{ height: embedded ? '360px' : '400px', width: '100%' }}
      />
    ) : (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: embedded ? '360px' : '400px' }}
      >
        <p className="text-muted mb-0">No data available for visualization</p>
      </div>
    );

  if (embedded) {
    return (
      <div className="w-100">
        <h5 className="mb-1 fw-semibold">{title}</h5>
        {description ? <p className="mt-0 mb-2 text-body-secondary small">{description}</p> : null}
        {chartBlock}
      </div>
    );
  }

  return (
    <Card className="h-100">
      <Card.Body>
        <h4 className="mb-0">{title}</h4>
        {description ? <p className="mt-1 mb-2 text-muted small">{description}</p> : null}
        {chartBlock}
      </Card.Body>
    </Card>
  );
};

export default BiasMLChart;
